import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, Renderer2, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RepairLineItemsFormBulk } from '../../../form-model/damage-and-repair.form.model';
import { RepairLineItemFormWithParts } from '../../../form-model/cwo-repair-line-item.form.model';
import { PartLineItemForm } from '../../../form-model/part-line-item.form.model';
import { MaintenanceOrderPart } from '../../../models/maintenance-order-part';
import { BulkWorkOrderSharedDataService } from '../../../services/bulk-work-order-shared-data.service';
import { catchError, combineLatest, EMPTY, forkJoin, ReplaySubject, Subscription, switchMap, take, takeUntil, timer } from 'rxjs';
import { MasterDataService } from '../../../services/master-data.service';
import { Repair } from '../../../models/repair.model';
import { CodeDescription } from '../../../models/code-description.model';

import { BulkWorkOrderRepairLineItemComponent } from './bulk-work-order-repair-line-item/bulk-work-order-repair-line-item.component';
import { RepairLocation } from '../../../models/repair-location.model';
import { getTPIText, ThirdPartyIndicator } from '../../../models/tpi.model';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { Shop } from '../../../models/shop.model';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { BulkWorkOrderRepairLineItem } from '../../../models/bulk-work-order/bulk-work-order-repair-line-item.model';
import { reapirCodeAndRepairLocationUniqueValidator } from '../../../shared-validators/repair-code-and-repair-location-unique.validator';
import { mutualExclusiveRepairCodeValidator } from '../../../shared-validators/mutual-exclusive-repair-code.validator';
import { RepairExclusion } from '../../../models/repair-exclusion.model';
import { PartExclusionRule } from '../../../models/part-exclusion-rule.model';
import { mutualExclusivePartValidator } from '../../../shared-validators/mutual-exclusive-part.validator';
import { ErrorComponent } from '../../../shared-components/error/error.component';
import { ContainerGradeValidator } from './bulk-work-order-repair-line-item/container-grade.validator';
import { ToastrService } from '../../../services/toastr-service';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { ContainerSizeValidator } from './bulk-work-order-repair-line-item/container-size.validator';
import { ContainerManufacturerValidator } from './bulk-work-order-repair-line-item/container-manufacturer.validator';
import { repairCodePieceLimitValidator } from '../../../shared-validators/repair-code-piece-limit.validator';
import { repairCodeContractValidator } from '../../../shared-validators/repair-code-contract.validator';
import { Mode } from '../../../models/mode.model';
@Component({
  selector: 'app-bulk-work-order-repair-line-items',
  imports: [ReactiveFormsModule, BulkWorkOrderRepairLineItemComponent, LoaderComponent, ErrorComponent],
  templateUrl: './bulk-work-order-repair-line-items.component.html',
  styleUrl: './bulk-work-order-repair-line-items.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BulkWorkOrderRepairLineItemsComponent implements OnInit, OnDestroy {
  repairLineItemsForm!: FormGroup<RepairLineItemsFormBulk>;
  repairs = signal<Repair[]>([]);
  damages = signal<CodeDescription[]>([]);
  repairLocations = signal<RepairLocation[]>([]);
  tpiCodes = signal<ThirdPartyIndicator[]>([]);
  modeAndShopSelected = signal(false);
  apiCallsForMasterData = signal<APICallStatus>('NOT_STARTED');
  selectedShop = signal<Shop | null>(null);
  selectedMode = signal<Mode | null>(null);
  repairExclusions: RepairExclusion[] = [];
  partExclusions: PartExclusionRule[] = [];
  private repairAndDamageCodesApiCallSubscription: Subscription | undefined;
  constructor(
    public bulkWorkOrderSharedDataService: BulkWorkOrderSharedDataService,
    public masterDataService: MasterDataService,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private toastrService: ToastrService,
    private applicationTextService: ApplicationtTextService,
    private containerGradeValidator: ContainerGradeValidator,
    private containerSizeValidator: ContainerSizeValidator,
    private readonly containerManufacturerValidator: ContainerManufacturerValidator,
  ) {
    this.bulkWorkOrderSharedDataService.Reset.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.initializeOrResetRepairLineItemsForm();
      this.repairLineItems.push(this.getSingleRepairLineItem());
    });
  }
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  ngOnInit(): void {
    combineLatest([this.bulkWorkOrderSharedDataService.Mode, this.bulkWorkOrderSharedDataService.Shop])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([mode, shop]) => {
        if (mode && shop) {
          this.selectedShop.set(shop);
          this.selectedMode.set(mode);
          this.modeAndShopSelected.set(true);
          this.apiCallsForMasterData.set('IN_PROGRESS');
          this.initializeOrResetRepairLineItemsForm();
          this.repairAndDamageCodesApiCallSubscription?.unsubscribe();
          this.masterDataService.clearRepairCodesCache();
          this.loadRepairLineItemData(mode, shop);
        }
      });
  }
  private loadRepairLineItemData(mode: Mode, shop: Shop): void {
    this.repairAndDamageCodesApiCallSubscription = timer(100)
      .pipe(
        switchMap(() =>
          forkJoin([
            this.masterDataService
              .getRepairCodes(mode.code, shop.maintenanceShopCode, shop.maintenanceShopCurrency.exchangeRateFromUsd)
              .pipe(take(1)),
            this.masterDataService.getDamageCodes(mode.damageType).pipe(take(1)),
            this.masterDataService.getRepairLocations().pipe(take(1)),
            this.masterDataService.getRepairTpiCodes().pipe(take(1)),
            this.masterDataService.getRepairExclusions(mode.code).pipe(take(1)),
            this.masterDataService.getPartExclusions(mode.code).pipe(take(1)),
          ]),
        ),
        catchError(() => {
          this.apiCallsForMasterData.set('FAILED');
          this.toastrService.showToastr('Failed to load repair line item data, including part exclusions', 'error');
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(([repairs, damages, repairLocations, tpiCodes, exclusionCodes, partExclusions]) => {
        this.repairs.set(this.filterRepairsForBulkWorkOrder(repairs));
        this.damages.set(damages);
        this.repairLocations.set(repairLocations.filter((x) => x.damageType === mode.damageType));
        this.tpiCodes.set(tpiCodes);
        this.apiCallsForMasterData.set('COMPLETED_SUCCESFULLY');
        this.repairLineItems.push(this.getSingleRepairLineItem());
        this.repairExclusions = exclusionCodes;
        this.partExclusions = partExclusions;
        this.addValidatorsToRepairLineItemsFormGroup();
      });
  }
  get repairLineItems(): FormArray<FormGroup<RepairLineItemFormWithParts>> {
    return this.repairLineItemsForm.get('repairLineItems') as FormArray<FormGroup<RepairLineItemFormWithParts>>;
  }

  private addValidatorsToRepairLineItemsFormGroup(): void {
    this.repairLineItemsForm.clearValidators();
    this.repairLineItemsForm.addValidators([
      reapirCodeAndRepairLocationUniqueValidator(),
      mutualExclusiveRepairCodeValidator(this.repairExclusions),
      mutualExclusivePartValidator(this.partExclusions),
      repairCodePieceLimitValidator(this.repairs(), this.toastrService, this.applicationTextService),
      repairCodeContractValidator(this.repairs(), this.applicationTextService),
    ]);
  }
  private filterRepairsForBulkWorkOrder(repairs: Repair[]): Repair[] {
    return repairs.filter((x) => x.multipleEq === true && x.isActive === true && x.isSuspended === false);
  }
  private initializeOrResetRepairLineItemsForm(): void {
    this.reset();
    this.subscribeToChangeAndWriteDataToSharedService();
    this.subscribeToFromStatusChange();
  }

  private reset(): void {
    this.repairLineItemsForm = new FormGroup<RepairLineItemsFormBulk>({
      repairLineItems: new FormArray<FormGroup<RepairLineItemFormWithParts>>([]),
    });
    this.repairLineItemsForm.controls.repairLineItems.reset();
    this.bulkWorkOrderSharedDataService.BulkWorkOrderRepairLineItems = [];
  }

  private subscribeToChangeAndWriteDataToSharedService(): void {
    this.repairLineItemsForm.controls.repairLineItems.valueChanges.subscribe((val) => {
      this.containerGradeValidator.validate(val.map((x) => x.repairCode ?? '')).subscribe((result) => {
        this.bulkWorkOrderSharedDataService.ContainerGradeValidationErrors = result;
      });
      this.containerSizeValidator
        .validate(
          val.map((x) => x.repairCode ?? ''),
          this.repairs(),
        )
        .subscribe((result) => {
          this.bulkWorkOrderSharedDataService.ContainerSizeValidationErrors = result;
        });
      this.containerManufacturerValidator
        .validate(
          val.map((x) => x.repairCode ?? ''),
          this.repairs(),
        )
        .subscribe((result) => {
          this.bulkWorkOrderSharedDataService.ManufacturerValidationErrors = result;
        });
      const lineItemsWithMandatoryFields = val?.filter((x) => x.repairCode && x.repairLocationCode && x.damageCode && x.tpiCode);
      const value: BulkWorkOrderRepairLineItem[] = lineItemsWithMandatoryFields.map((repairLineItem) => {
        const excludePreparationHours = this.repairs().find((x) => x.code === repairLineItem.repairCode)?.excludePreparationHours ?? true;
        const maintenanceOrderParts: MaintenanceOrderPart[] = (repairLineItem.parts ?? []).map((part) => ({
          partNumber: part.partNumber ?? '',
          unitQuantity: part.pieces ?? 0,
          unitPrice: part.partCost ?? 0,
          currentSerialNumbers: part.currentSerialNumbers ?? [],
        }));
        return {
          repairQuantity: repairLineItem.pieces ?? 0,
          manHoursPerPiece: repairLineItem.manHoursPerPiece ?? 0,
          excludePreparationHours: excludePreparationHours,
          materialCostPerPiece: repairLineItem.materialCostPerPiece ?? 0,
          equipmentDamageCode: repairLineItem.damageCode ?? '',
          maintenanceTypeCode: repairLineItem.repairCode ?? '',
          maintenanceRepairLocationCode: repairLineItem.repairLocationCode ?? '',
          thirdPartyIndicator: getTPIText(repairLineItem.tpiCode ?? ''),
          maintenanceOrderParts,
        };
      });
      this.bulkWorkOrderSharedDataService.BulkWorkOrderRepairLineItems = value;
    });
  }

  private subscribeToFromStatusChange(): void {
    this.repairLineItemsForm.controls.repairLineItems.statusChanges.subscribe((status) => {
      this.bulkWorkOrderSharedDataService.RepairLineItemFormStatus = status;
    });
  }

  private getSingleRepairLineItem(): FormGroup<RepairLineItemFormWithParts> {
    let repairLineItem = this.formBuilder.nonNullable.group<RepairLineItemFormWithParts>({
      damageCode: this.formBuilder.nonNullable.control<string>(''),
      repairCode: this.formBuilder.nonNullable.control<string>(''),
      repairLocationCode: this.formBuilder.nonNullable.control<string>(''),
      tpiCode: this.formBuilder.nonNullable.control<string>(''),
      pieces: this.formBuilder.nonNullable.control<number>(0),
      materialCostPerPiece: this.formBuilder.nonNullable.control<number>(0),
      manHoursPerPiece: this.formBuilder.nonNullable.control<number>(0),
      itemTotalCost: this.formBuilder.nonNullable.control<string>({ value: '0', disabled: true }),
      parts: this.formBuilder.nonNullable.array<FormGroup<PartLineItemForm>>([]),
    }) as FormGroup<RepairLineItemFormWithParts>;
    return repairLineItem;
  }
  onRemoveRepairLineItem(index: number) {
    if (this.repairLineItems.length === 1) {
      this.toastrService.showToastr(this.applicationTextService.AtLeastOneRepairLineItemRequired, 'warning');
      return;
    }
    const repairCode = this.repairLineItems.at(index).controls.repairCode.value;
    this.bulkWorkOrderSharedDataService.ClearContainerGradeValidationError(repairCode);
    this.bulkWorkOrderSharedDataService.ClearContainerSizeValidationError(repairCode);
    this.bulkWorkOrderSharedDataService.ClearManufacturerValidationError(repairCode);
    this.repairLineItems.removeAt(index);
  }
  onAddRepairLineItemClicked() {
    this.repairLineItems.push(this.getSingleRepairLineItem());
    let length = this.repairLineItems.length - 1;
    if (length > 0) {
      timer(200).subscribe((x) => {
        let id = `#repair-line-item${length}-damage-code`;
        let element = this.renderer.selectRootElement(id);
        element.focus();
      });
    }
  }
}
